import { apiClient } from '../../utils/api';
import {
  SuperAdminUser,
  ListUsersRequest,
  ListUsersResponse,
  CreateUserRequest,
  UpdateUserRequest,
  ManagePermissionOverridesRequest,
  UserPermissionOverride
} from '../../types/super-admin';

export class UserService {
  private static readonly BASE_PATH = '/super-admin/users';

  static async listUsers(params: ListUsersRequest = {}): Promise<ListUsersResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.tenantId) queryParams.append('tenantId', params.tenantId);
    if (params.role) queryParams.append('role', params.role);
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const url = `${this.BASE_PATH}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiClient.get<ListUsersResponse>(url);
  }

  static async getUser(userId: string): Promise<SuperAdminUser> {
    return apiClient.get<SuperAdminUser>(`${this.BASE_PATH}/${userId}`);
  }

  static async createUser(userData: CreateUserRequest): Promise<SuperAdminUser> {
    return apiClient.post<SuperAdminUser>(this.BASE_PATH, userData);
  }

  static async updateUser(userId: string, updates: UpdateUserRequest): Promise<SuperAdminUser> {
    return apiClient.put<SuperAdminUser>(`${this.BASE_PATH}/${userId}`, updates);
  }

  static async deleteUser(userId: string): Promise<void> {
    return apiClient.delete<void>(`${this.BASE_PATH}/${userId}`);
  }

  static async resetPassword(userId: string, options: { sendEmail?: boolean; temporaryPassword?: string } = {}): Promise<{ temporaryPassword?: string }> {
    return apiClient.post<{ temporaryPassword?: string }>(`${this.BASE_PATH}/${userId}/reset-password`, options);
  }

  static async toggleUserStatus(userId: string, status: 'active' | 'inactive' | 'suspended'): Promise<SuperAdminUser> {
    return apiClient.put<SuperAdminUser>(`${this.BASE_PATH}/${userId}/status`, { status });
  }

  static async transferUser(userId: string, targetTenantId: string): Promise<SuperAdminUser> {
    return apiClient.put<SuperAdminUser>(`${this.BASE_PATH}/${userId}/transfer`, { tenantId: targetTenantId });
  }

  static async getUserPermissionOverrides(userId: string): Promise<UserPermissionOverride[]> {
    return apiClient.get<UserPermissionOverride[]>(`${this.BASE_PATH}/${userId}/permission-overrides`);
  }

  static async managePermissionOverrides(userId: string, request: ManagePermissionOverridesRequest): Promise<UserPermissionOverride[]> {
    return apiClient.post<UserPermissionOverride[]>(`${this.BASE_PATH}/${userId}/permission-overrides`, request);
  }

  static async removePermissionOverride(userId: string, overrideId: string): Promise<void> {
    return apiClient.delete<void>(`${this.BASE_PATH}/${userId}/permission-overrides/${overrideId}`);
  }

  static async bulkUpdateUsers(userIds: string[], updates: Partial<UpdateUserRequest>): Promise<{ success: string[]; failed: string[] }> {
    return apiClient.post<{ success: string[]; failed: string[] }>(`${this.BASE_PATH}/bulk-update`, {
      userIds,
      updates
    });
  }

  static async exportUsers(filters?: ListUsersRequest): Promise<{ downloadUrl: string }> {
    const queryParams = new URLSearchParams();
    
    if (filters?.tenantId) queryParams.append('tenantId', filters.tenantId);
    if (filters?.role) queryParams.append('role', filters.role);
    if (filters?.status) queryParams.append('status', filters.status);
    if (filters?.search) queryParams.append('search', filters.search);

    const url = `${this.BASE_PATH}/export${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiClient.get<{ downloadUrl: string }>(url);
  }

  static async getUserAuditHistory(userId: string, page = 1, limit = 20): Promise<{
    logs: Array<{
      id: string;
      timestamp: string;
      action: string;
      changes: Record<string, any>;
      performedBy: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    return apiClient.get<any>(`${this.BASE_PATH}/${userId}/audit-history?page=${page}&limit=${limit}`);
  }
}